import _ from 'lodash';
import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	Convert,
	convertIntialState,
	convertStepsStatus,
	convertSteps,
	ConvertId,
	ConvertDetails,
	ConvertData,
	ConversionResponse,
	ConvertSchedule,
	convertType,
} from 'constants/convert.constants';
import { formValue, intialMessage, userMessage } from 'constants/ui.constants';

interface step {
	status: convertStepsStatus;
}

type steps = {
	[key in convertSteps]: step;
};

type obj = { [key: string]: string };

interface convertState {
	items: Convert[];
	loading: boolean;
	message: userMessage;
	upsertingProcess: boolean;
	upsertedConvert: Convert;
	activeStep: convertSteps;
	steps: Partial<steps>;
	currentConversionData: ConvertData;
	currentConversionResponse: ConversionResponse;
	expired: boolean;
}

const intialStep = { status: convertStepsStatus.notStarted };

const intialSteps = () => {
	let s: Partial<steps> = {};
	_.values(convertSteps).forEach((k) => {
		s[k] = intialStep;
	});
	return s;
};

const initialState: convertState = {
	items: [],
	loading: false,
	message: intialMessage,
	upsertingProcess: false,
	upsertedConvert: convertIntialState,
	activeStep: convertSteps.DETAILS,
	steps: intialSteps(),
	currentConversionData: {},
	currentConversionResponse: {},
	expired: false,
};

export const convertSlice = createSlice({
	name: 'convert',
	initialState,
	reducers: {
		setCurrentConversionResponse: (state, action: PayloadAction<ConversionResponse>) => {
			state.currentConversionResponse = action.payload;
		},
		setConversionData: (state, action: PayloadAction<ConvertData>) => {
			state.currentConversionData = action.payload;
		},
		setLoading: (state, action: PayloadAction<boolean>) => {
			state.loading = action.payload;
		},
		setExpired: (state, action: PayloadAction<boolean>) => {
			state.expired = action.payload;
		},
		setMessage: (state, action: PayloadAction<userMessage>) => {
			state.message = action.payload;
		},
		toggleUpserting: (state, action: PayloadAction<boolean>) => {
			state.upsertingProcess = action.payload;
			state.upsertedConvert = convertIntialState;
			state.steps = intialSteps();
			state.activeStep = convertSteps.DETAILS;
		},
		intializeConvert: (state, action: PayloadAction<Convert>) => {
			state.upsertedConvert = action.payload;
		},
		upsertConvert: (state, action: PayloadAction<Partial<Convert>>) => {
			state.upsertedConvert = { ...state.upsertedConvert, ...action.payload };
		},
		setSteps: (state, action: PayloadAction<Partial<steps>>) => {
			state.steps = { ...state.steps, ...action.payload };
		},
		setActiveStep: (state, action: PayloadAction<convertSteps>) => {
			state.activeStep = action.payload;
		},
		setOptionalDates: (state, action: PayloadAction<string[]>) => {
			state.upsertedConvert.optionalDates = action.payload;
		},
		setCutOffTime: (state, action: PayloadAction<any>) => {
			state.upsertedConvert.cutOffTime = action.payload;
		},
		setConvert: (state, action: PayloadAction<formValue>) => {
			if (action.payload.name.includes('.')) {
				const path = action.payload.name.split('.');
				if (_.has(state.upsertedConvert, path[0])) {
					let nested = _.get(state.upsertedConvert, path[0]);
					state.upsertedConvert = {
						...state.upsertedConvert,
						//@ts-ignore
						[path[0] as keyof Convert]: { ...nested, [path[1]]: action.payload.value },
					};
				} else {
					state.upsertedConvert = {
						...state.upsertedConvert,
						[path[0] as keyof Convert]: { [path[1]]: action.payload.value },
					};
				}
			} else {
				state.upsertedConvert = {
					...state.upsertedConvert,
					[action.payload.name]: action.payload.value,
				};
			}
		},
		resetConvert: (state, action: PayloadAction<convertType>) => {
			state.upsertedConvert = {
				...convertIntialState,
				type: action.payload,
			};
		},
	},
});

export const {
	setLoading,
	setMessage,
	setSteps,
	setActiveStep,
	toggleUpserting,
	intializeConvert,
	setConvert,
	setConversionData,
	setCurrentConversionResponse,
	setOptionalDates,
	setCutOffTime,
	upsertConvert,
	setExpired,
	resetConvert,
} = convertSlice.actions;
const convertSelector = (state) => state.convert;
export const isLoading = (state: any) => state.convert.loading;
export const getMessage = (state: any) => state.convert.message;
export const isCreatingProcess = (state: any) => state.convert.upsertingProcess;
export const getNewConvert = (state: any) => state.convert.upsertedConvert;
export const getSteps = (state: any) => state.convert.steps;
export const getCurrentConversionData = (state: any) => state.convert.currentConversionData;
export const getExpried = (state: any) => state.convert.expired;
export const getCurrentConversionResponse = (state: any) => state.convert.currentConversionResponse;
export const getActiveStep = (state: any) => {
	return _.indexOf(_.values(convertSteps), state.convert.activeStep);
};

export const getConverts = (state: any) => {
	return state.convert.items;
};

export const getConvertById = (id: string) => (state: any) => {
	return _.find(state.convert.items, { [ConvertId]: id });
};

export const getConvertDetails = createSelector([convertSelector], (state: any): obj => {
	let obj: obj = {};
	let bn = state.upsertedConvert;
	ConvertDetails.forEach((key) => (obj[key] = bn[key] ? String(bn[key]) : ''));
	return obj;
});

export const getConvertSchedule = createSelector([convertSelector], (state: any): obj => {
	let obj: obj = {};
	let bn = state.upsertedConvert;
	ConvertSchedule.forEach((key) => (obj[key] = bn[key] ? String(bn[key]) : ''));
	return obj;
});

export default convertSlice.reducer;
